import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
// import {useTranslation} from "react-i18next";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@mui/material/Button";
import EmptyData from "@/components/EmptyData";
import MainActions from "@/components/MainActions";
import VLFModal from "@/components/Modal";
import HandleExerciseModal from "@/pages/Manager/Match/handleExercise";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import {ExerciseAPI} from "@/api/exercise";
import {TrainingAPI} from "@/api/training";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";

import "./index.scss";

function TrainingExercisesPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	// const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [trainingData, setTrainingData] = useState({});

	const [openExerciseModal, setOpenExerciseModal] = useState(false);

	const [initialData, setInitialData] = useState();
	const [idToDelete, setIdToDelete] = useState();

	const getTraining = async () => {
		setLoading(true);
		try {
			const response = await TrainingAPI.getById(id);
			setTrainingData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getTraining();
	}, [id]);

	const handleClose = () => {
		setOpenExerciseModal(false);
		setInitialData();
	};

	const [trainingExercises, setTrainingExercises] = useState([]);
	const getTrainingExercises = async (resetExercises = false) => {
		if (resetExercises) setLoading(true);
		try {
			if (resetExercises) {
				const deleted = trainingExercises.find((e) => e.id === idToDelete);

				const promises = [];
				trainingExercises
					?.filter((e) => e.id !== idToDelete && e.phase === deleted.phase)
					?.sort((a, b) => a.sorting - b.sorting)
					?.forEach((e, s) => {
						promises.push(
							TrainingAPI.updateDetail(trainingData?.id, e.id, {
								...e,
								sorting: s + 1,
							}),
						);
					});

				await Promise.all(promises);
			}

			const response = await TrainingAPI.getDetails(id);
			setTrainingExercises(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getTrainingExercises();
	}, [id]);

	const deleteExercise = async () => {
		setLoading(true);
		try {
			await TrainingAPI.deleteDetail(trainingData?.id, idToDelete);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleConfirm(false);
			getTrainingExercises(true);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleExerciseOrder = async (detail, direction) => {
		const newSorting =
			direction === "up" ? detail.sorting - 1 : detail.sorting + 1;
		const exerciseToMove = trainingExercises.find(
			(e) => e.sorting === newSorting,
		);
		setLoading(true);
		try {
			await TrainingAPI.updateDetail(trainingData?.id, detail.id, {
				...detail,
				sorting: newSorting,
			});
			await TrainingAPI.updateDetail(trainingData?.id, exerciseToMove.id, {
				...exerciseToMove,
				sorting: detail.sorting,
			});
			getTrainingExercises();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handleExercise = async (data, exerciseId) => {
		setLoading(true);
		try {
			let body = {
				trainingId: id,
			};

			if (data.currentExercise) {
				body = {
					...body,
					...data,
					exerciseId: data?.currentExercise?.value,
				};
				delete body.currentExercise;
				delete body.exercise;
			} else {
				const response = await ExerciseAPI.create({
					...data,
					description: data.description || "",
					exerciseType: data.exerciseType || "",
					basicActivities: data.basicActivities,
					goalkeepers: data.goalkeepers,
				});

				body = {
					...body,
					...data,
					exerciseId: response.id,
				};
			}

			body.phase = data.currentPhase.value;

			exerciseId
				? await TrainingAPI.updateDetail(trainingData?.id, exerciseId, body)
				: await TrainingAPI.createDetail({
						...body,
						sorting:
							trainingExercises?.filter(
								(te) => parseInt(te.phase) === data.currentPhase.value,
							)?.length + 1,
					});
			getTrainingExercises();
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/trainings/" + id),
			color: "info",
		},
		{
			icon: <AddIcon />,
			onClick: () => {
				setOpenExerciseModal(true);
			},
		},
	];

	const breadcrumbsElements = [
		{label: "Allenamenti", path: "/trainings"},
		{label: "/"},
	];

	const sections = [
		{
			label: "Momento 1",
			data: trainingExercises
				?.sort((a, b) => a.sorting - b.sorting)
				?.filter((te) => te.phase === "1"),
		},
		{
			label: "Momento 2",
			data: trainingExercises
				?.sort((a, b) => a.sorting - b.sorting)
				?.filter((te) => te.phase === "2"),
		},
		{
			label: "Momento 3",
			data: trainingExercises
				?.sort((a, b) => a.sorting - b.sorting)
				?.filter((te) => te.phase === "3"),
		},
		{
			label: "Momento 4",
			data: trainingExercises
				?.sort((a, b) => a.sorting - b.sorting)
				?.filter((te) => te.phase === "4"),
		},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					trainingData?.startDate
						? moment(trainingData.startDate).format("DD/MM/YYYY HH:mm") +
							" - " +
							moment(trainingData.endDate).format("HH:mm")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin g-1 training-exercises-container">
				{sections.map((s, k) => {
					return (
						<div key={k} className="col-12 col-lg-6">
							<div className="phase-container">
								<div className="phase-title">{s.label}</div>
								<div className="phase-details">
									{s.data?.map((te, k) => {
										return (
											<div key={k} className="phase-exercise">
												<div className="image">
													<img
														src={
															te.exercise?.imageUrl ||
															require("@/assets/images/manager/exercise.png")
														}
														className={!te.exercise?.imageUrl ? "default" : ""}
														alt=""
													/>
												</div>
												<div className="text">
													<div className="name">
														<div>{te.sorting + " - " + te.exercise?.name}</div>
														<div>{te.duration + "'"}</div>
													</div>
													<div className="paragraph">
														{te.notes ? te.notes : "Nessuna nota inserita"}
													</div>
												</div>
												<div className="actions">
													<Button
														variant={"outlined"}
														onClick={() => {
															handleExerciseOrder(te, "up");
														}}
														size="small"
														disabled={k <= 0}
													>
														<div className="action-content d-flex align-items-center">
															<ArrowUpwardIcon />
														</div>
													</Button>
													<Button
														variant={"outlined"}
														onClick={() => {
															handleExerciseOrder(te, "down");
														}}
														size="small"
														disabled={k >= s.data?.length - 1}
													>
														<div className="action-content d-flex align-items-center">
															<ArrowDownwardIcon />
														</div>
													</Button>
													<Button
														variant={"outlined"}
														onClick={() => {
															setInitialData(te);
															setOpenExerciseModal(true);
														}}
														size="small"
													>
														<div className="action-content d-flex align-items-center">
															<EditIcon />
														</div>
													</Button>
													<Button
														variant={"outlined"}
														onClick={() => {
															setIdToDelete(te.id);
															setOpenHandleConfirm(true);
														}}
														size="small"
														color="error"
													>
														<div className="action-content d-flex align-items-center">
															<CloseIcon />
														</div>
													</Button>
												</div>
											</div>
										);
									})}
									{!s.data?.length && <EmptyData />}
								</div>
							</div>
						</div>
					);
				})}
			</div>
			{openExerciseModal ? (
				<HandleExerciseModal
					open={openExerciseModal}
					initialData={initialData}
					confirm={handleExercise}
					close={handleClose}
				/>
			) : null}
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={deleteExercise}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default TrainingExercisesPage;
